import Fuse from 'fuse.js';

const normalize = (s) =>
  (s ?? '')
    .toString()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const makeAccessor = (getText) =>
  typeof getText === 'function' ? getText : (x) => x?.[getText] ?? '';

function bucketize(queryNorm, items, accessor) {
  const exact = [], wordStart = [], substring = [], rest = [];

  for (const item of items) {
    const text = accessor(item);
    const norm = normalize(text);
    if (!norm) { rest.push(item); continue; }

    if (norm === queryNorm) { exact.push(item); continue; }

    const words = norm.split(/[\s\-_/.,;:()]+/).filter(Boolean);
    if (words.some((w) => w.startsWith(queryNorm))) { wordStart.push(item); continue; }

    if (norm.includes(queryNorm)) { substring.push(item); continue; }

    rest.push(item);
  }

  return { exact, wordStart, substring, rest };
}

function makeFuse(items) {
  return new Fuse(items, {
    includeScore: true,
    ignoreLocation: true,
    threshold: 0.34,
    minMatchCharLength: 2,
    keys: ['__norm'],
  });
}

export function fuzzySearchRanked(query, items, getText = 'label', maxResults = 50) {
  if (!query || !items?.length) return items ?? [];
  const accessor = makeAccessor(getText);
  const q = normalize(query.trim());
  if (!q) return items;

  // Attach normalized + raw forms
  const prepared = items.map((item) => ({
    ...item,
    __text: accessor(item),
    __norm: normalize(accessor(item)),
  }));

  const { exact, wordStart, substring, rest } = bucketize(q, prepared, (x) => x.__text);

  const fuse = makeFuse(rest);
  const fuzzy = fuse.search(q).map((r) => r.item);

  const merged = [...exact, ...wordStart, ...substring, ...fuzzy];
  return merged.slice(0, maxResults);
}

export const fuzzySearch = (query, target) =>
  fuzzySearchRanked(query, [{ text: target }], 'text', 1).length > 0;
